// The last bank-gateway trip of this tab. Coming back without paying (bank cancel/error → Back,
// often onto another patient page) opens a sheet that continues payment on the same reserved book
// instead of leaving the patient on a raw invoice whose button would reserve a new one.
// The backend holds the unpaid book ~21 min; stop offering it a little before that.
export const PENDING_PAYMENT_TTL_MS = 18 * 60_000;

const KEY = 'factor_pending_payment';

export interface PendingPayment {
  bookId: string;
  centerId: string;
  paymentMethod?: string;
  /** Payable amount in toman at the gateway trip (VAT included) — for "too expensive" alternatives. */
  priceToman?: number;
  /** Factor page the trip started from — the sheet reopens there on every return. */
  factorPath: string;
  at: number;
  /** Already shown once on some other page; don't chase the patient around the site. */
  shownElsewhere: boolean;
}

const write = (data: PendingPayment) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    // Storage blocked — the site just behaves as before.
  }
};

export const savePendingPayment = (data: Omit<PendingPayment, 'at' | 'shownElsewhere'>, now = Date.now()) =>
  write({ ...data, at: now, shownElsewhere: false });

// Paid books, shared by all tabs: some gateways (SnappPay, blu) open the receipt in a NEW tab, so the
// factor tab that started the trip never sees it and would claim the payment failed.
const PAID_KEY = 'factor_paid_books';
const PAID_KEEP_MS = 60 * 60_000;

const paidBooks = (now = Date.now()): Record<string, number> => {
  try {
    const all = JSON.parse(localStorage.getItem(PAID_KEY) || '{}') as Record<string, number>;
    return Object.fromEntries(Object.entries(all).filter(([, at]) => now - at < PAID_KEEP_MS));
  } catch {
    return {};
  }
};

/** Called by the receipt: this book is paid — no tab may offer to "continue paying" it. */
export const markBookPaid = (bookId: string, now = Date.now()) => {
  try {
    localStorage.setItem(PAID_KEY, JSON.stringify({ ...paidBooks(now), [bookId]: now }));
  } catch {}
};

export const PAID_BOOKS_STORAGE_KEY = PAID_KEY;

export const readPendingPayment = (now = Date.now()): PendingPayment | undefined => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return undefined;
    const data = JSON.parse(raw) as PendingPayment;
    if (!data?.bookId || typeof data.at !== 'number' || now - data.at > PENDING_PAYMENT_TTL_MS) return undefined;
    if (paidBooks(now)[data.bookId]) return undefined;
    return data;
  } catch {
    return undefined;
  }
};

export const markPendingPaymentShown = () => {
  const data = readPendingPayment();
  if (data) write({ ...data, shownElsewhere: true });
};

export const clearPendingPayment = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {}
};

/** Online visit only: its reasons and offers (other online doctors, response time) are wrong for a clinic turn. */
export const ONLINE_VISIT_CENTER_ID = '5532';

export const shouldOpenPendingPaymentSheet = (data: PendingPayment, path: string) => {
  if (String(data.centerId) !== ONLINE_VISIT_CENTER_ID) return false;
  // Only on the factor page the trip started from — never on other pages (owner, 2026-10-08).
  return path === data.factorPath;
};
